import { useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { useMountEffect } from 'hooks/useMountEffect'
import { PageContainer } from 'components/MobileSearch/MobileSearch.styles'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { MobileVariantCard } from 'components/products/MobileVariantCard'
import {
  useVariantsCatalogInfinite,
  type VariantCatalog,
  type VariantsCatalogResponse,
} from 'services/products'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

/** Mobile products surface: search the variant catalog and view/download spec-sheet PDFs in the field (FIR-7497). */
export function MobileProductsPage() {
  const [search, setSearch] = useState('')
  // Ref-based debounce keeps the typed value snappy while throttling the
  // query param that drives data fetching (no useEffect for data).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Clear a pending debounce timer on unmount so it can't fire afterwards.
  useMountEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  })

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim())
    }, SEARCH_DEBOUNCE_MS)
  }

  const trimmedSearch = debouncedSearch || undefined

  const infiniteQuery = useVariantsCatalogInfinite(
    { search: trimmedSearch },
    PAGE_SIZE
  )

  return (
    <PageContainer>
      <div className="px-4 pt-4">
        <h1 className="mb-3 text-xl font-semibold text-fg">Products</h1>
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-secondary"
          />
          <input
            type="search"
            inputMode="search"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search name, SKU, GTIN, or code"
            aria-label="Search products"
            className="w-full rounded-md border border-line bg-surface py-2 pl-10 pr-3 text-sm text-fg placeholder:text-fg-secondary focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <MobileInfiniteScrollView<VariantsCatalogResponse, VariantCatalog>
        extractItems={(page) => page.results}
        infiniteQuery={infiniteQuery}
      >
        {(variants) =>
          variants.map((variant) => (
            <MobileVariantCard key={variant.id} variant={variant} />
          ))
        }
      </MobileInfiniteScrollView>
    </PageContainer>
  )
}
